import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';

import { ActivatedRoute, Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { LMBaseComponent } from '@base/lm-base.component';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { SohoModalDialogService } from 'ids-enterprise-ng';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMSpecService } from '@services/lm-spec.service';
import { LMFormManualRepService } from '@form/lm-form-manual-rep.service';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMBrowserUtilService, LMContextUtilService, LMGlobalUtilService, LMUniqueIdService } from '@utils/index';
import { LMRouteComponentService } from '@services/lm-route-component.service';
import { LMComponentInfoService, LMErrorUtilService, LMUrlInfoBuilderService } from '@services/index';
import { LMContextService } from '@services/lm-context.service';
import { BehaviorSubject, Subject, Subscription, SubscriptionLike as ISubscription } from 'rxjs';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMBannerMenuService } from '@banner/lm-banner-menu.service';
import {
  LMActionFormDialogResult,
  LMActionFormDialogResultCode,
  LMActionFormDialogService
} from '@modal/lm-action-form-dialog.service';
import * as urltemplate from 'url-template';
import { AppConstants } from '@utils/app.constants';
import { Location } from '@angular/common';
import { LMNavigationService } from '@navigation/lm-navigation.service';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMCtrlShiftClickEvent } from '@base/index';
import { LMHtmlUrlDocService } from '@services/lm-html-url-doc.service';
import { LMManualRepresentationComponent } from '@app/components';
import { LMAppComponentService } from '@utils/lm-app-component.service';

export class LMUrlToastMessage {
  public messageTitle: string = null;
  public message: string = null;
}

export class LMUrlDocSaveResult {
  private _isComplete: boolean;
  private _message: string = null;
  private _isError = false;

  get isComplete(): boolean {
    return this._isComplete;
  }

  get isError(): boolean {
    return this._isError;
  }

  get hasMessage(): boolean {
    return this._message !== null;
  }
  get message(): string {
    return this._message;
  }

  constructor(isComplete: boolean, isError: boolean,
    message?: string) {
    this._isComplete = isComplete;
    this._isError = isError;
    if (message) {
      this._message = message;
    }
  }
}

@Component({
  selector: 'lm-html-url-doc',
  template: `
    <ng-template [ngIf]="iFrameSecurityException">
      <div soho-emptymessage [class.l-vertical-center]="'true'" [emptyMessageOptions]="crossOriginMessage"></div>
    </ng-template>
    <ng-template [ngIf]="!iFrameSecurityException">
        <div style="width: 100%; height: 100%">
      <iframe
          attr.id="{{testVersion1 ? parentContainerId : parentContainerId + '_iframe'}}"
          [attr.src]="customUrl"
          [attr.identifier]="frameIdentifier"
          attr.data-automation-id="lm-{{automationId}}-iframe"
          style="height: 100%; width: 100%"
      ></iframe>
      </div>
    </ng-template>
    <div #urlDialogPlaceholder id="urlDialogPlaceholder"></div>
  `,
  styles: [ `:host {
    display: block;
    height: 100%;
  }` ]
})
export class LMHtmlUrlDocComponent extends LMBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('urlDialogPlaceholder', { read: ViewContainerRef, static: true }) urlPlaceHolder: ViewContainerRef;

  @HostListener('window:resize') public onResize() {
    this.setDefaultFrameHeight();
  }

  @Input() bottomSpacing = 0;
  @Input() upToLines?: 0;
  @Input() upToChar?: 0;
  @Input() adjustFrameToWindow = true;
  @Input() isPanelNavigation = false;
  @Input() isDashboardPanel = false;
  @Input() fieldManualRep = false;
  @Input() parentContainerId: string;
  @Input() showLoading = false;
  @Input() frameIdentifier: string;
  @Input() automationId: string;

  // this is only set when an html fragment is needed. Default is false and used this way except for fragment loading
  // [isHtmlFragment] must be listed before [url] from calling component due to order process.
  @Input() set isHtmlFragment(isHtmlFragment: boolean) {
    this._isHtmlFragment = isHtmlFragment;
  }
  @Input() set url(url: string) {
    // this.customUrl = this.getCustomURL();
    // remove ng=true and ng3x=true if it is in the url
    this.inputUrl = url;
    this._url = url
      .replace(/&csk.ng=true/g, '')
      .replace(/csk.ng=true&/g, '')
      .replace(/&csk.ng3x=true/g, '')
      .replace(/csk.ng3x=true&/g, '')
      .replace(/&csk.showusingxi=true/g, '')
      .replace(/csk.showusingxi=true&/g, '');

    // const iframe = this.elementRef.nativeElement.getElementsByTagName('iframe');
    //  iframe.src = this.getCustomURL(); // this._sanitizer.bypassSecurityTrustResourceUrl(this.getCustomURL());
    const noDefaultPortUrl = this.lmUrlUtilService.stripDefaultPort(this._url);
    let host = this.lmContextUtilService.getLocation(noDefaultPortUrl).host;

    // We use the getLocation method on the contextUtilService to allow us to get the host from the url.
    // Even when we pass in the default port stripped from the url to the method when you ask for the host
    // it includes the port(IE only). So we look for the default port and strip it if it is at the the end of the host.
    // The browsers strip default ports so they cannot be used to compare to window.location.host
    // This will allow the code to take the proper path when loading the iframe below
    if (this.lmBrowserUtil.isIE) {
      host = (host.endsWith(':80')) ? host.substring(0, host.length - 3) : host;
      host = (host.endsWith(':443')) ? host.substring(0, host.length - 4) : host;
    }

    const standAloneParam = this.lmUrlUtilService.getUrlParam(url, 'standalone') || this.isManualRep(url);

    const log = {
      codeLoc: 'lm-html-url-doc.component.td - set url @Input',
      url,
      localUrl: this._url,
      noDefaultPortUrl,
      host,
      standAloneParam,
      'window.location.host': window.location.host,
      'url.length': url.length,
      doHostsMatch: (host.toLowerCase() === window.location.host.toLowerCase()),
    };

    this.lmLoggerService.log(log);

    if (url.length > 0 && host.toLowerCase() === window.location.host.toLowerCase()) {
      const hashPos = url.indexOf('#');
      let hash = '';
      if (hashPos >= 0) {
        const searchPos = url.indexOf('?');
        hash = searchPos >= 0 ? url.substring(hashPos, searchPos) : url.substring(hashPos);
      }

      if (standAloneParam && standAloneParam === 'true') {
        // NOOP at this point we will not insert the landmark-api.js file. We might decide to in the future.
      } else if (this._isHtmlFragment) {
        const dataArea = this.route.snapshot.params['dataArea'];
        const webApp = this.route.snapshot.params['webApp'];

        const destinationUrl = this.fixedEncodeURIComponent(this.lmUrlUtilService.stripDefaultPort(this._url));
        const customUrl = window.location.protocol + '//' + window.location.host.toLowerCase() + '/' + dataArea + '/' + webApp + '/custom/custom';
        this._url = customUrl + '?csk.3x=true&loadUrl=' + destinationUrl;
        this._url = this.lmUrlUtilService.propagateSessionKeys(this._url, '&');
        // remove ng and showusingxi again after propagatingSessionKeys (basically to propagate lidkey)
        this._url = this._url.replace(/&csk.ng=true/g, '').replace(/csk.ng=true&/g, '').replace(/&csk.ng3x=true/g, '').replace(/csk.ng3x=true&/g, '').replace(/&csk.showusingxi=true/g, '')
          .replace(/csk.showusingxi=true&/g, '');
        if (this.fieldManualRep) {
          this._url += '&manRepType=field';
        }
      }
      // if the hosts match add the 'internal' parameter to let the server know to set the iframe option to the servlet host.
      this._url = this.lmUrlUtilService.addParamToUrl(this._url, 'internal', 'true');
      // now if a hash value exists on the incoming url we tack it onto the custom url to have it available to an AngularJS
      // or other app passing info in the hash while loading.
      if (hash.length > 0){
        this._url += hash;
      }
    }

    this.loadedUrl = this._url;

    const sanitizedUrl = this._sanitizer.bypassSecurityTrustResourceUrl( this._url); // this.getCustomURL()
    // this.customUrl = sanitizedUrl;
    this.customUrl = sanitizedUrl;

    // after loading the url we wait a second and then try to access the iframe contentWindow.
    // This process will set a flag that triggers a message to display if a security exception is hit.
    // This would be a Cross-site Origin issue.
    setTimeout(() => {
      this.getContentWindow();
    }, 1000);
  }

  @Output() contentModified = new EventEmitter<any>();
  @Output() htmlUrlDisplayed = new EventEmitter<any>();
  @Output() setFieldDirty = new EventEmitter<any>();
  @Output() titleChanged = new EventEmitter<boolean>();
  @Output() landmarkApiLoaded = new EventEmitter<any>();
  @Output() customDocumentLoaded = new EventEmitter<any>();
  @Output() landmarkCustomReady = new EventEmitter<any>();
  @Output() customButtonsSet = new EventEmitter<any>();
  @Output() changeToolbarDisabledState = new EventEmitter<any>();
  @Output() notifyUrlTransactionComplete = new EventEmitter<any>();

  private innerIframe: NodeListOf<HTMLIFrameElement>;
  private _isHtmlFragment = false;
  private _manualRepFields = {};
  private _url: string;
  private customButtons: Array<LMToolbarButton>;
  public iFrameSecurityException = false;
  public iFrameSizingElement: HTMLElement;
  private defaultFrameHeight = 600;
  private minimumFrameHeight = 350;
  public crossOriginMessage: SohoEmptyMessageOptions = {
    title: 'Error loading web page',
    icon: 'icon-empty-error-loading',
    info: 'Cross-site origin not allowed.',
    color: 'azure'
  };
  private formType: LMFormType = 'Standard';

  private localThemeSubjectSubscription;
  private localColorSubjectSubscription;
  public readonly localThemeSubject = new Subject();
  public readonly localColorSubject = new Subject();
  private isRouterOutletRootComponent = false;

  /**
   * Used internally so that the subscription can be released when
   * the component is destroyed.
   */
  private routerEventsSubscription: ISubscription;

  public testVersion1: boolean;

  public customUrl: SafeResourceUrl = null;
  public loadedUrl: string = null;
  public inputUrl: string = null;
  constructor(
    public sohoModalDialogService: SohoModalDialogService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected _sanitizer: DomSanitizer,
    protected lmErrorUtilService: LMErrorUtilService,
    private location: Location,
    private lmContextService: LMContextService,
    private lmThemeService: LMThemeService,
    private lmLoggerService: LMLoggerService,
    @Inject(forwardRef(() => LMActionFormDialogService)) private lmActionFormDialogService, // LMActionFormDialogService,
    private router: Router,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private routeComponent: LMRouteComponentService,
    private lmSpecService: LMSpecService,
    private elementRef: ElementRef,
    private lmNavigationService: LMNavigationService,
    private currentComponent: LMRouteComponentService,
    private lmUrlUtilService: LMUrlUtilService,
    private changeDetectorRef: ChangeDetectorRef,
    private lmFormManualRepService: LMFormManualRepService,
    private lmHtmlUrlDocService: LMHtmlUrlDocService,
    private lmContextUtilService: LMContextUtilService,
    private lmBrowserUtil: LMBrowserUtilService,
    protected lmComponentInfoService: LMComponentInfoService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    protected lmUniqueIdService: LMUniqueIdService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmBannerMenuService: LMBannerMenuService,
    protected lmAppComponentService: LMAppComponentService
  )  {
    super(sohoModalDialogService, lmBusyIndicatorService, lmComponentInfoService, lmUniqueIdService, lmAppComponentService);

    this.routerEventsSubscription = router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.lmLoggerService.log('lm-html-url-doc: navigationend');
      } else if (event instanceof NavigationStart) {
        this.lmLoggerService.log('lm-html-url-doc: navigationstart');
      }
    });
  }

  // events
  ngOnInit() {

    this.localColorSubjectSubscription =
      this.lmThemeService.getColorChangeSubject().subscribe(color => this.localColorSubject.next(color));
    this.localThemeSubjectSubscription =
      this.lmThemeService.getThemeChangeSubject().subscribe(theme => this.localThemeSubject.next(theme));

    this.localColorSubject.subscribe( (color) => {
      this.getLandmarkApiRef().fireLandmarkColorChanged(color);
    });
    this.localThemeSubject.subscribe( (theme) => {
      this.getLandmarkApiRef().fireLandmarkThemeChanged(theme);
    });

    this.lmLoggerService.trace([ this, 'ngOnInit()' ]);
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;

    // todo: remove once testversion 1 is no longer supported.
    //  This is to maintain backward compatability when no parentContainerId is set.
    //  This is wrong as there could be duplicate ids but I don't want to break any
    //  existing tests.
    if (this.testVersion1 && !this.parentContainerId) {
      this.parentContainerId = 'landmark3_5frame';
    }

    if (!this.frameIdentifier) {
      this.frameIdentifier = this.testVersion1 ? this.parentContainerId : this.parentContainerId  + '_iframe';
    }

    if (this.showLoading) {
      this.showBusyIndicator();
    } else {
      this.hideBusyIndicator();
    }
    this.placeHolder = this.urlPlaceHolder;
  }

  ngAfterViewInit(): void {

    $(window.document).off('titleChangedEvent').on('titleChangedEvent', (e: any) => {
      this.titleChanged.emit(e);
    });
    $(window.document).off('customDocumentLoadedEvent').on('customDocumentLoadedEvent', (e: any) => {
      this.customDocumentLoaded.emit(this);
    });
    $(window.document).off('lmApiLoadEvent').on('lmApiLoadEvent', (e: any) => {
      try {
        const loadApi = e.detail.api;
        const frameIdentifier = e.detail.identifier;
        let apiSpace: {[key: string]: any};
        // We take a very indirect path to make sure we match up the correct api with the correct hand coded screen or manual rep

        // We need to tie the correct lm-html-url-doc to the calling landmark-api.js so information is correct
        // this has not been done in the past and it works because for the most part the hand coded screens to this point
        // have not had a need to be that specific. The need was discovered when Manual Rep was implemented and many
        // iframe/html-url-doc's are on the same page and they are always tied to a set of fields from the lpl definition.
        const refFrame = this.lmHtmlUrlDocService.getFrameRefs()[frameIdentifier];
        if (!refFrame) {
          apiSpace = this.getFormApi();
        } else {
          apiSpace = refFrame.compRef.getLmHtmlUrlDocComponent().getFormApi();
        }
        loadApi(apiSpace);
        this.landmarkApiLoaded.emit(this);
        this.hideBusyIndicator();
      } catch (error) {
        this.lmLoggerService.log('Unable to add api method set to docueents using landmark-api');
      }
    });

    $(window.document).off('IFrameUnloadedEvent').on('IFrameUnloadedEvent', (e: any) => {
      this.lmLoggerService.log('*** screen unloading ***');
    });

    $(window.document).off('IFrameContentModifiedEvent').on('IFrameContentModifiedEvent', (e: any) => {
      this.contentModified.emit(e);
      // here we would find the angular event for content modified
      // alert(this.checkModified(e));
    });

    this.lmLoggerService.trace([ this, 'ngAfterViewInit' ]);
    this.routeComponent.setHtmlUrlDocComponent(this);
    this.build();

    this.htmlUrlDisplayed.emit(this);

    const inPage = this.lmComponentInfoService.closest(this.elementRef.nativeElement, 'lm-page');
    const inList = this.lmComponentInfoService.closest(this.elementRef.nativeElement, 'lm-list');
    const inForm = this.lmComponentInfoService.closest(this.elementRef.nativeElement, 'lm-form');

    if (!inPage && !inList && ! inForm) {
      this.isRouterOutletRootComponent = true;
    }

    // need to be ceative on when we do this
    setTimeout(() => {
      this.setDefaultFrameHeight();
    }, 1);

  }

  ngOnDestroy() {

    /**
     * Only call un-subscribe from routerOutletSubscription on ngOnDestory
     * since it's created in the constructor and is necessary when
     * ListComponent object is being re-used.
     */
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }

    // added to try to fix memory leak in IE11 when using the harness
    $(window.document).off('titleChangedEvent');
    $(window.document).off('lmApiLoadEvent');
    $(window.document).off('IFrameUnloadedEvent');
    $(window.document).off('IFrameContentModifiedEvent');

    if (this.localColorSubject) {
      this.localColorSubject.unsubscribe();
    }
    if (this.localThemeSubject) {
      this.localThemeSubject.unsubscribe();
    }
    this.routeComponent.setHtmlUrlDocComponent(null);
  }

  public getFormApi() {
    return {
      fieldManualRepInfo: this.fieldManualRep ? { isFieldManualRep: true, defaultLineHeight: AppConstants.DEFAULT_LINE_HEIGHT, lines: this.upToLines } : { isFieldManualRep: false },
      isNg: () => true,
      closeNavigationMenu: () => {this.closeNavigationMenu();},
      getManualRefFieldsInternal: (identifier: string) => this.getManualRefFields(identifier),
      getFormStateInternal: (identifier: string) => this.getFormState(identifier),
      getContext: () => this.getContext(),
      closeFormDialogs: () => this.closeFormDialogs(),
      getThemeInfo: () => this.getThemeInfo(),
      showToast: (message: string, messageTitle?: string) => this.showToast(message, messageTitle),
      setModelFieldValueInternal: (identifier: string, fieldId: string, value: any, notifyListeners?: boolean) => {
        this.ngZone.run(() => {
          this.setModelFieldValue(identifier, fieldId, value, notifyListeners);
        });
      },
      notifyTransactionComplete: (toastMessage?: LMUrlToastMessage) => {
        this.notifyTransactionComplete(toastMessage);
      },
      setDataView: (dataView) => {},
      landmarkCustomReady: () => {
        this.ngZone.run(() => this.landmarkCustomReady.emit());
      },
      setRouteUrl: (url: string, sendContextMessages?: boolean) => {
        this.setRouteUrl(url, sendContextMessages);
      },
      setSizingElement: (element) => (this.setSizingElement(element)),
      setCustomToolbarButtons: (buttonData) => {

        try {
          const toolBarButtons: Array<LMToolbarButton> = buttonData as Array<LMToolbarButton>;
          // If the button is an icon button and it is a menu button we will supress the down arrow on the menu button by dafault
          // If the value is set we will honor it so it still could be false if that is desired.
          for (const toolBarButton of toolBarButtons) {
            const buttonInfo = toolBarButton.customToolbarButtonInfo;
            if (!buttonInfo.text && buttonInfo.menu && buttonInfo.hideMenuArrow === undefined) {
              buttonInfo.hideMenuArrow = true;
            }
          }
          this.customButtons = buttonData; this.ngZone.run(() => this.customButtonsSet.emit(buttonData));

        } catch (err) {
          this.lmLoggerService.log('Unable to process buttons: Need to be of type Array<LMToolbarButton>');
        }
      },
      setToolbarDisabledState: (state) => {
        this.ngZone.run(() => {
          this.changeToolbarDisabledState.emit(state);
        });
      },
      sendClientStatsTelegraf: (name: string, telegrafProperties: LMTelegrafProperties) => this.sendClientStatsTelegraf(name, telegrafProperties),
      getCustomToolbarButtons: () => this.customButtons,
      save: (callback) => {callback(true, false, '');},
      setFieldDirty: (dirty: boolean) => {this.ngZone.run(() => {this.runSetFieldDirty(dirty);});},
      isModified: (): boolean => false,
      runMenuItem: (menuUrl: string) => {this.runMenuItem(menuUrl);},
      runNavigationItem: (navigationUrl: string) => {this.runNavigationItem(navigationUrl);},
      runDrillItem: (drillUrl: string) => {this.runDrillItem(drillUrl);},
      runUrl: (url: string) => {this.runUrl(url);},
      runMobileUrl: (url: string) => {this.runMobileUrl(url);},
      replaceUrl: (url: string) => {this.replaceUrl(url);},
      log: (value: any) => { this.lmLoggerService.log(value); },
      isDashboardPanel: () => this.isDashboardPanel,
      openActionForm: (webapp: string, prodline: string, resourceId: string, search: string, fields: LMHarnessInterfaceFields, actionName?: string, hideActionButtons?: boolean) => [
        this.ngZone.run(() => {
          this.openForm(webapp, prodline, resourceId, search, fields, actionName, hideActionButtons);
        }) ],
      openForm: (webapp: string, prodline: string, resourceId: string, search: string, fields: LMHarnessInterfaceFields, actionName?: string, hideActionButtons?: boolean) => [
        this.ngZone.run(() => {
          this.openForm(webapp, prodline, resourceId, search, fields, actionName, hideActionButtons);
        }) ],
      runActionItem: (url: string) => {
        this.ngZone.run(() => {
          this.processActionItem(url);
        });
      },
      run3xAction: (url: string) => {
        this.run3xAction(url);
      },
      execute3_5MinglEvent: (method: string, param: any) => this.execute3_5MinglEvent(method, param),
      showComponentInfo: () => {
        this.showComponentInfo();
      },
    };
  }

  public build() {
  }

  public execute3_5MinglEvent(method: string, param: any): any {
    if (method === 'registerMessageHandlers') {
      this.lmContextUtilService.registerMessageHandlers();
    } else if (method === 'clearAll') {
      this.lmContextUtilService.clearAll();
    } else if (method === 'disableShortcut') {
      this.lmContextUtilService.disableShortcut();
    } else if (method === 'enableShortcut') {
      this.lmContextUtilService.enableStandardShortcut();
    } else if (method === 'sendContextMessages') {
      this.lmContextUtilService.sendContextMessages(param);
    } else if (method === 'sendMessagesForConfigHome') {
      this.lmContextUtilService.sendMessagesForConfigHome(param);
    }
  }

  public reload() {
    // must allow the angular stuff to finish before firing the event to the frame
    setTimeout(() => {
      this.getLandmarkApiRef().fireLandmarkDataUpdated();
    }, 1);

    //   this.ngZone.runOutsideAngular(() => this.getLandmarkApiRef().fireLandmarkDataUpdated());
  }

  public showComponentInfo() {
    const event = document.createEvent('MouseEvents');
    event.initMouseEvent('click', true, true, window, 0, 0, 0, 80, 20, true, false, true, false, 0, null);
    //  document.body.dispatchEvent(event);
    const ctrlShiftClickEvent: LMCtrlShiftClickEvent = event as LMCtrlShiftClickEvent;
    ctrlShiftClickEvent.srcEventElement = this.elementRef.nativeElement;

    ctrlShiftClickEvent.routeComponent = (this.currentComponent.getRouteComponent() as LMBaseComponent);

    this.elementRef.nativeElement.dispatchEvent(ctrlShiftClickEvent);
  }

  public getUserWebRequestId(): string {
    return  this.isRouterOutletRootComponent ? 'HtmlUrlDoc' + '-' +this.getBaseCompId()
      : this.getRouteCompUserWebRequestId();
  }

  public getComponentInfo(additionalInfo?: LMCompInfoAdditionalInfo): LMComponentInfo {
    // let compiledTemplate = urltemplate.parse(this.pageSpec);
    // let loadUrl = compiledTemplate.expand({ resourceId: this.model.getData().resourceId });

    const message = this.lmContextService.getContext() ? '' : 'CONTEXT NOT LOADED YET. ';
    let manualRepComp: LMManualRepresentationComponent = null;
    if (this.frameIdentifier && this.fieldManualRep) {
      manualRepComp = this.lmHtmlUrlDocService.getFrameRefs()[this.frameIdentifier].compRef;
    }


    const formInfo: LMComponentInfo = {
      componentType: this.fieldManualRep ? 'MANUAL_REP' : 'HTMLDOC',
      name: this.fieldManualRep ? manualRepComp._manualRep.repName : this.parentContainerId,
      userWebRequestId: this.getUserWebRequestId(),
      type: this.fieldManualRep ? 'Manual Representation' : 'HTML Page',
      developer: this.lmContextService.getContext().developer,
      loadedUrl: this._url,
      inputUrl: this.inputUrl,
      bottomSpacing: this.bottomSpacing,
      adjustFrameToWindow: this.adjustFrameToWindow,
      isDashboardPanel: this.isDashboardPanel,
      fieldManualRep: this.fieldManualRep,
      uniqueId: this.parentContainerId,
      showLoading: this.showLoading,
      //  manualRepFields: this.manualRepFields,
      isHtmlFragment: this._isHtmlFragment,
      tenant: this.lmContextService.getContext().dataArea,
      hasConfigAccess: this.lmContextService.hasConfigAccess(),

      message: message.length > 0 ? message : undefined,
    };

    if (formInfo.fieldManualRep) {
      formInfo.manualRepName = manualRepComp._manualRep.repName;
      formInfo.frameIdentifier = this.frameIdentifier;
    }

    return formInfo;
  }

  public getCustomURL() {
    if (this._url) { // this._url;
      return 'http://usspnswolynie2.infor.com:2041/uitest/web/samples/iframetest/test.html';
    } else {
      return null;
    }
  }
  private fixedEncodeURIComponent(str: string) {  // tslint:disable-line
    return encodeURIComponent(str).replace(/[!'()*]/g, c => '%' + c.charCodeAt(0).toString(16));
  }

  public getContentWindow() {
  //  let contentWindow: any = document.getElementById(this.parentContainerId);
    const findStr = '[identifier="' + this.frameIdentifier + '"]';
    const contentWindows: any = document.querySelectorAll(findStr);
    const contentWindow = contentWindows.length > 0 ? contentWindows[0] : undefined;

    if (!contentWindow) {
      return undefined;
    }
    return contentWindow.contentWindow;
  }

  private setSizingElement(element: HTMLElement) {
    this.iFrameSizingElement = element;
    this.setDefaultFrameHeight();
  }

  private setRouteUrl(url: string, sendContextMessages = true) {
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);
    this.location.replaceState(url);

    if (sendContextMessages) {
      this.lmContextUtilService.sendMessagesForHtmlPage({
        dataArea: this.lmContextService.getUrlPdlOrDa(),
        webApp: this.lmContextService.getContext().webApp
      });
    }
  }

  private hasLandmarkApiRef(): boolean {
    try {
      const contentWindow = this.getContentWindow();
      // we will have no landmarkApiRef if the content window does nto exist or we have hit an iFrameSecurityException
      if (!contentWindow || this.iFrameSecurityException) {
        return false;
      }
      return contentWindow !== undefined && contentWindow.landmarkNg !== undefined && contentWindow.landmarkNg.api !== undefined;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  private getLandmarkApiRef() {
    if (this.iFrameSecurityException) {
      this.lmLoggerService.log('Security error on iframe detected');
      return null;
    }
    const contentWindow = this.getContentWindow();
    if (contentWindow && contentWindow.landmarkNg) {
      return contentWindow.landmarkNg.api;
    }

    return null;
  }

  public getRouterUrl() {
    return this.router.url;
  }

  public getLoadedUrl() {
    return this.loadedUrl;
  }

  public setLoadedUrl(loadedUrl: string) {
    this.loadedUrl = loadedUrl;
  }

  public setModelFieldValue(frameIdentifier: string, fieldId: string, value: any, notifyListeners?: boolean) {
    notifyListeners = (notifyListeners === undefined) ? true : notifyListeners;
    this.lmHtmlUrlDocService.getFrameData(frameIdentifier).compRef.setModelFields(fieldId, value, notifyListeners);
  }

  public getFormState(identifier) {
    const data = this.lmHtmlUrlDocService.getFrameData(identifier);
    return data.compRef.getFormState();
  }

  public getManualRefFields(identifier: string) {

    const data = this.lmHtmlUrlDocService.getFrameData(identifier);
    return data.manualRepFields;
    // let name = this.getLandmarkApiRef().manualRepName;
    // return this.lmFormManualRepService.getManualRefFields(name);
  }

  public runActionFormIn3_5(url: string) {
    // fix url to run in 3x

    // todo sjw ppatton would be nice to use LMUrlUtilService.updateUrlParams for removing params from a url.
    //   however in updateUrlParams we  need to implement the ability to remove a param if undefined|null is
    //   passed in as the value to LMUrlParam property
    // const params: LMUrlParams = {
    //   'csk.cli': undefined,
    //   'csk.ng': undefined,
    //   'csk.ng3x': undefined,
    //   'csk.3x': true
    // };
    // this.lmUrlUtilService.updateUrlParams(url, params);

    url = url
      .replace(/&csk.cli=true/g, '')
      .replace(/&csk.ng=true/g, '')
      .replace(/csk.ng=true&/g, '')
      .replace(/&csk.ng3x=true/g, '')
      .replace(/csk.ng3x=true&/g, '');

    const delimiter: string = url.indexOf('?') === -1 ? '?' : '&';
    url = url + delimiter + 'csk.3x=true';
    this.getContentWindow().runActionForm(url);

    // Since we know we are going to a 3.5 action form we close the 4.0 busy indicator as we call the 3.5 form and let 3.5 handle this beyond that point.
    this.hideBusyIndicator();
  }

  public replaceUrl(url: string) {
    let fixedUrl = url;

    fixedUrl = this.lmUrlUtilService.remove3XParams(fixedUrl);
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(fixedUrl, 'custom-replaceUrl');
    fixedUrl = this.lmUrlUtilService.updateContextSessionKeyParams(fixedUrl);
    fixedUrl = this.checkForRedirect(fixedUrl);
    fixedUrl = this.lmUrlUtilService.toRelativeUrl(fixedUrl);

    this.lmGlobalUtilService.navigateByUrl(fixedUrl, { replaceUrl: true }).then((completed?: boolean) => {
      this.lmLoggerService.log('navigationByUrl completed?: ' + completed);
    });
  }

  public get3_5Url() {
    return this._url;
  }

  public isManualRep(url: string): boolean {
    return url.indexOf('manualrep/manualrep') >= 0;
  }

  public runMobileUrl(url: string) {
    this.runUrl(url, false, true);
  }

  public runUrl(url: string, newWindow?: boolean, maintainWindowCSK?: boolean) {

    if (this.currentComponent.getHtmlUrlDocComponent() === this) {
      // get url path of current and future url.
      const futureUrl = url;
      const currentUrl = this.get3_5Url();

      const futurePath = this.lmUrlUtilService.getUrlPath(futureUrl);
      const currentPath = this.lmUrlUtilService.getUrlPath(currentUrl);

      if (futurePath === currentPath) {
        this.getLandmarkApiRef().fireSameUrlRequested(this.lmUrlUtilService.getUrlParams(futureUrl));
        return;
      }

    }
    let fixedUrl = url;
    if (!maintainWindowCSK) {
      fixedUrl = this.lmUrlUtilService.remove3XParams(fixedUrl);
    } else {
      fixedUrl = this.lmUrlUtilService.remove3XParam(fixedUrl);
    }

    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(fixedUrl, 'custom-runUrl');

    fixedUrl = this.lmUrlUtilService.updateContextSessionKeyParams(fixedUrl);
    fixedUrl = this.lmUrlUtilService.stripDefaultPort(fixedUrl);
    fixedUrl = this.checkForRedirect(fixedUrl);

    const extUrlCheck = this.lmUrlUtilService.toRelativeUrl(fixedUrl);
    if (newWindow || extUrlCheck.indexOf('http') === 0) {
      this.lmUrlUtilService.openExternalUrl(fixedUrl);
    } else {
      if (this.lmContextService.inMingleWidget()) {
        this.lmContextUtilService.sendDrillbackMessage(url);
      } else {
        this.ngZone.run(() => {
          this.lmGlobalUtilService.navigateByUrl(fixedUrl).then((completed?: boolean) => {
            this.lmLoggerService.log('navigationByUrl completed?: ' + completed);
          });
        });
      }
    }
  }

  public fireSameUrlRequested(url) {
    this.hideBusyIndicator();
    const params = this.lmUrlUtilService.getUrlParams(url);
    // this.location.go( url);
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);

    this.getLandmarkApiRef().fireSameUrlRequested(params);
  }

  private checkForRedirect(url: string): string {
    if (url.indexOf('/action/') >= 0) {
      return this.processActionItem(url);
    }
    return url;
  }

  public run3xAction(actionUrl: string) {
    actionUrl = this.lmUrlUtilService.remove3XParams(actionUrl);
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(actionUrl, 'custom-run3xAction');
    actionUrl = this.lmUrlUtilService.updateContextSessionKeyParams(actionUrl);
    this.lmSpecService.getRedirectUrl(actionUrl).subscribe(response => {
      const url = response.redirectUrl;
      //    url = this.lmUrlUtilService.updateContextSessionKeyParams(url, this.lmContextService.getContext())
      this.lmGlobalUtilService.navigateByUrl(url);
    });
  }

  public runDrillItem(drillUrl: string) {
    drillUrl = this.lmUrlUtilService.remove3XParams(drillUrl);
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(drillUrl, 'custom-runDrillItem');
    drillUrl = this.lmUrlUtilService.updateContextSessionKeyParams(drillUrl);
    this.lmSpecService.getDrillSpec(drillUrl).subscribe(
      response => {
        if (response.redirectUrl) {
          let url = response.redirectUrl;
          url = this.lmUrlUtilService.updateContextSessionKeyParams(url);
          this.lmGlobalUtilService.navigateByUrl(url);
        }
      },
      error => {
        this.lmErrorUtilService.handleUnexpectedError(error);
      });
  }

  public closeFormDialogs() {
    while(this.lmActionFormDialogService.hasOpenDialog) {
      this.lmActionFormDialogService.getOpenDialog.close();
    }
  }

  // This will always open a XI action form if called
  public openForm(webapp: string, prodline: string, resourceId: string, search: string, harnessFields: LMHarnessInterfaceFields, actionName?: string, hideActionButtons?: boolean) {

    let actionFormUrl = '/' + prodline + '/' + webapp + '/form/' + resourceId  + '?' + search
      + (search && search.length > 0 ? '&' : '')
      + (actionName ? 'action=' + actionName : '');
    actionFormUrl = this.lmUrlUtilService.propagateSessionKeys(actionFormUrl, '&');

    actionFormUrl = this.lmUrlUtilService.remove3XParams(actionFormUrl);
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(actionFormUrl, 'custom-openActionForm');

    this.lmSpecService.getFormSpec(actionFormUrl).subscribe((response: LMFormSpecResponse | LMSystemException) => {

      const formUrlInfo = (response as LMFormSpecResponse).formUrlInfo;
      const formSpec = (response as LMFormSpecResponse).formSpec;

      let actionSpec: LMActionSpec = formSpec.busClassSpec.actionSpec;
      if (!actionSpec) {
        let index = 0;
        formSpec.busClassSpec.actions.forEach(data => {
          if (data.name === actionName) {
            index = data.index;
          }
        });
        actionSpec = formSpec.busClassSpec.actions[index];
      }

      const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
      let url = urlCompiledTemplate.expand({ resourceId, actionName: actionSpec.name });

      // now add the fields passed in to the url params to be added to dataview.
      const fieldParamString = this.lmUrlUtilService.fieldsToUrlParams(harnessFields, 'f');
      url += fieldParamString;

      this.lmActionFormDialogService.getActionFormDialogDataView(url).subscribe((responseDV: any) => {
        const formDataView: LMDataView = responseDV;
        if (formDataView.status === AppConstants.STATUS_COMPLETED) {

          const options: LMActionFormOptions = {
            forceXI: true,
          };
          this.lmActionFormDialogService
            .runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView, undefined, options, false, hideActionButtons)
            .subscribe((actionFormDialogResult: LMActionFormDialogResult) => {
              if (actionFormDialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
              // The view exception and errors will be prompted before the dialog closes.
              // So just print the success message here after the dialog closes
                const transaction = actionFormDialogResult.transactionStateResult.completedTransactionList[ 0 ];
                const actionMessage = transaction.message;
                this.lmGlobalUtilService.displayToastMessage('', actionMessage, AppConstants.TRA_ACTION_COMPLETED);

                this.fireFormActionComplete(LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED,
                  { message: actionMessage, resourceId: transaction.dataView.resourceId });

                // Check to see if the form return call includes a redirectUrl. If so navigate to that url.
                setTimeout(() => {
                  const formResponse = actionFormDialogResult.transactionStateResult.response[0];
                  if (formResponse.redirectUrl) {
                    this.lmGlobalUtilService.navigateByUrl(formResponse.redirectUrl);
                  }
                }, 1);
              }
            });
        } else {
          this.lmLoggerService.error(responseDV);
          this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
        }
      });
    });
  }

  public fireFormActionComplete(status: LMActionFormDialogResultCode, data: LMActionFormCompleteData) {
    if (this.hasLandmarkApiRef()) {
      const apiRef = this.getLandmarkApiRef();
      // fire an event in the landmark-api to inform the calling code that the action was completed.
      if (apiRef) {
        this.ngZone.run(() => {
          apiRef.fireFormActionComplete(status, data);
        });
      }
    }
  }

  public processActionItem(actionUrl: string): string {
    actionUrl = this.lmUrlUtilService.remove3XParams(actionUrl);
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(actionUrl, 'custom-processActionItem');
    actionUrl = this.lmUrlUtilService.updateContextSessionKeyParams(actionUrl);
    this.lmSpecService.getActionSpec(actionUrl).subscribe(
      response => {
        if (response.redirectUrl) {
          let url = response.redirectUrl;
          url = this.lmUrlUtilService.updateContextSessionKeyParams(url);
          if (this.lmContextService.inMingleWidget()) {
            this.lmContextUtilService.sendDrillbackMessage(url);
          } else {
            this.lmGlobalUtilService.navigateByUrl(url);
          }
        }
      },
      error => {
        this.lmErrorUtilService.handleUnexpectedError(error);
      });

    return actionUrl;
  }

  public sendClientStatsTelegraf(name: string, telegrafProperties: LMTelegrafProperties): Subscription  {
    return this.lmGlobalUtilService.sendClientStatsTelegraf(telegrafProperties, true, name).subscribe((response: any) => {});
  }

  public runNavigationItem(navigationUrl: string) {
    navigationUrl = this.lmUrlUtilService.remove3XParams(navigationUrl);
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(navigationUrl, 'custom-runNavigationItem');
    navigationUrl = this.lmUrlUtilService.updateContextSessionKeyParams(navigationUrl);
    this.lmSpecService.getNavigationSpec(navigationUrl).subscribe(
      response => {
        if (response.redirectUrl) {
          let url = response.redirectUrl;
          url = this.lmUrlUtilService.updateContextSessionKeyParams(url);

          // put pdf in its own tab
          if (url.indexOf('output=pdf') >= 0 || url.indexOf('/document/') >= 0 ) {
            this.lmUrlUtilService.openFile(url);
          } else if (this.lmContextService.inMingleWidget()) {
            this.lmContextUtilService.sendDrillbackMessage(url);
          } else {
            this.lmGlobalUtilService.navigateByUrl(url);
          }

        }
      },
      error => {
        this.lmErrorUtilService.handleUnexpectedError(error);
      });
    //
    //
    //
    // this.lmSpecService.getRedirectUrl(navigationUrl).first().subscribe(response => {
    //   this.lmUrlUtilService.navigateByUrl(response.redirectUrl);

  }

  public runMenuItem(menuUrl: string) {
    let fixedMenuLink = menuUrl;

    if (fixedMenuLink.indexOf('/menu/') < 0) {
      this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(fixedMenuLink, 'custom-runMenuItem');
      this.lmGlobalUtilService.navigateByUrl(fixedMenuLink).then((completed?: boolean) => {
        this.lmLoggerService.log('navigationByUrl completed?: ' + completed);
      });
    } else {
      fixedMenuLink = this.lmUrlUtilService.remove3XParams(fixedMenuLink);
      this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(fixedMenuLink, 'custom-runMenuItem');

      fixedMenuLink = this.lmUrlUtilService.updateContextSessionKeyParams(fixedMenuLink);
      const routeComponent: LMRouteComponentService = this.routeComponent;
      this.lmSpecService.getBannerSpec(fixedMenuLink).subscribe(
        response => {
          if (response.external) {
            this.lmUrlUtilService.openExternalUrl(response.redirectUrl);
          } else if (response.dialogUrl) {
            const personalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
            if (this.lmContextService.is3_5Content(personalization.appDisplay)) {
              const comp: LMHtmlUrlDocComponent = routeComponent.getHtmlUrlDocComponent();
              comp.runActionFormIn3_5(response.dialogUrl);
            } else {

              const url = this.lmUrlUtilService.updateContextSessionKeyParams(response.dialogUrl);
              this.lmBannerMenuService.requestActionFromPageMenu(url, response.resourceId, this.placeHolder);
            }
          } else if (response.redirectUrl) {
            const url = this.lmUrlUtilService.updateContextSessionKeyParams(response.redirectUrl);
            const noDefaultPortUrl = this.lmUrlUtilService.stripDefaultPort(url);
            this.ngZone.run(() => {
              this.lmGlobalUtilService.navigateByUrl(noDefaultPortUrl).then((completed?: boolean) => {
                this.lmLoggerService.log('navigationByUrl completed?: ' + completed);
              });
            });
          }
        },
        error => {
          this.lmLoggerService.log('Unable to find menu from 3.5 iframe menu call');
        });
    }
  }

  public showToast(message: string, messageTitle?: string) {
    this.lmGlobalUtilService.displayToastMessage(messageTitle, message, 'unknown');
  }

  public notifyTransactionComplete(toastMessage?: LMUrlToastMessage) {
    // emit the trans
    this.notifyUrlTransactionComplete.emit();
    this.showToast(toastMessage.message, toastMessage.messageTitle);
  }

  public getContext() {
    // this will need to be refined once we get back to the Manual Rep work.
    if (this.lmFormManualRepService.getContext()) {
      return this.lmFormManualRepService.getContext();
    } else {
      return this.lmContextService.getContext();
    }
  }

  public getThemeInfo() {
    const theme = this.lmThemeService.getAvailableThemes().filter((themeInstance) => {
      if (themeInstance.id === this.lmThemeService.getCurrentThemeId()) {
        return themeInstance;
      }
    });

    return { color: this.lmThemeService.getCurrentColor(), theme };
  }

  public getComponentPlaceHolder() {
    return this.placeHolder;
  }

  public setDataView(dataView: LMDataView) {
    if (this.hasLandmarkApiRef()) {
      this.getLandmarkApiRef().setDataView(dataView);
    }
  }

  public fireCustomButtonPressed(customToolbarButtonInfo: CustomToolbarButtonInfo) {
    if (this.hasLandmarkApiRef()) {
      this.getLandmarkApiRef().fireCustomButtonPressed(customToolbarButtonInfo);
    }
  }

  public runSave(): BehaviorSubject<LMUrlDocSaveResult> {
    const runSaveSubject = new BehaviorSubject<LMUrlDocSaveResult>(new LMUrlDocSaveResult(false, false));
    if (this.hasLandmarkApiRef()) {
      const callback = (success: boolean, error: any, message: string) => {
        // if the old callback signature is used we will adjust the fields before continuing with the code
        if (typeof error === 'string' || error instanceof String) {
          message = error as string;
          error = false;
        }
        this.ngZone.run(() => this.handleSave(success, error, message, runSaveSubject));
      };
      this.ngZone.runOutsideAngular(() => this.getLandmarkApiRef().save(callback));
    } else {
      runSaveSubject.next(new LMUrlDocSaveResult(true, false, ''));
      runSaveSubject.complete();
    }
    return runSaveSubject;

  }

  public fireLandmarkFormSaveComplete(successful: boolean) {
    const apiRef = this.getLandmarkApiRef();
    // fire an event in the landmark-api to inform the calling code that the action was completed.
    if (apiRef) {
      this.ngZone.run(() => {
        apiRef.fireLandmarkFormSaveComplete(successful);
      });
    } else {
      this.lmLoggerService.log('Unable to find apiRef needed to run fireLandmarkFormSaveComplete');
    }
  }

  private setDefaultFrameHeight() {
    const iframe = this.elementRef.nativeElement.getElementsByTagName('iframe');
    if (this.iFrameSizingElement) {
      const height = this.iFrameSizingElement.offsetHeight + 30;  // add just a little to cover for small errors
      $(iframe).css('height', height + 'px');
    } else if (this.upToChar > 0 || this.upToLines > 0) {
      // This will need to be changed. this is just for now to get it working.
      const fullFrameHeight = iframe[0].contentWindow.document.body ? iframe[0].contentWindow.document.body.scrollHeight : 0;
      const width = this.upToChar === 0 ? '100%' : ((this.upToChar * 10) + 'px');
      const height = this.upToLines === 0 ? AppConstants.DEFAULT_LINE_HEIGHT +'px' : (this.upToLines * AppConstants.DEFAULT_LINE_HEIGHT) + 'px';
      $(iframe).css('width', width);
      $(iframe).css('height', height);
      if (width !== fullFrameHeight && fullFrameHeight > 0) {
        $(iframe).css('height', fullFrameHeight);
      }
    } else if (this.adjustFrameToWindow) {
      const inMingleWidget = this.lmContextService.inMingleWidget();
      const isMobile = this.lmBrowserUtil.isMobile;
      const bottom: number  = isMobile ? this.bottomSpacing  : 0;
      let height = document.body.clientHeight - (this.elementRef.nativeElement.getElementsByTagName('iframe')[ 0 ].getBoundingClientRect().top);
      // if by chance the frame would be to small we will force a default height only do it for desktop
      if (!inMingleWidget && !isMobile && height < this.minimumFrameHeight) {
        height = (this.defaultFrameHeight - bottom);
      }

      if (this.isDashboardPanel || (isMobile && this.isPanelNavigation) ) {
        // center the embedded url media for dashboard panels
        $(iframe).css('height', 'inherit');
      } else {
        $(iframe).css('height', height + 'px');
      }
    }
  }

  private handleSave(success: boolean, error: boolean, message: string, observer: BehaviorSubject<LMUrlDocSaveResult>  ) {
    const result = new LMUrlDocSaveResult(success, error, message);
    observer.next(result);
    // observer.complete();
  }

  public runSetFieldDirty(dirty) {
    this.setFieldDirty.emit(dirty);
  }

  public isModified(): boolean {
    // this check is placed handle a situation where an iframe has a now Infor server document and we are looking to find a reference
    // to landmark-api. We catch this to avoid a possible security error of referencing a frame from another domain.
    try {
      return this.hasLandmarkApiRef() ? this.getLandmarkApiRef().isModified() : false;
    } catch (error) {
      return false;
    }
  }

  // public removeThisArea(e) {
  //   let element = $(e.srcElement).parent().remove();
  // }

  closeNavigationMenu() {
    const iframeWindow = this.getContentWindow();
    if (iframeWindow) {
      const parentDoc = iframeWindow.parent.document;
      // noinspection TypeScriptValidateTypes
      $(parentDoc).trigger('click.applicationmenu');
    }
  }

}
